import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteNavbar from "@/components/feature/SiteNavbar";
import SiteFooter from "@/components/feature/SiteFooter";
import NewsCard from "@/components/feature/NewsCard";
import Pagination from "@/components/feature/Pagination";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { newsList } from "@/mocks/news";
import { NEWS_PAGE_SIZE, paginate, totalPages } from "@/utils/content";

const categories = ["全部", ...Array.from(new Set(newsList.map((item) => item.category)))];

const sortOptions = [
  { key: "latest", label: "最新发布" },
  { key: "editor", label: "编辑推荐" },
];

export default function NewsListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const sort = searchParams.get("sort") || "latest";
  const category = searchParams.get("cat") || "全部";
  const page = Number(searchParams.get("page") || "1") || 1;

  useDocumentMeta(
    "动漫天堂 - 最新动漫资讯",
    "动漫天堂资讯频道，提供最新新番动态、制作访谈、剧情解析与行业消息，让你第一时间掌握动漫圈正在发生的事。",
    "动漫资讯,新番动态,动漫新闻,剧情解析,制作访谈,动漫天堂",
    "/news"
  );

  const filtered = useMemo(() => {
    const base =
      category === "全部"
        ? [...newsList]
        : newsList.filter((item) => item.category === category);
    if (sort === "latest") {
      base.sort((a, b) => (a.date < b.date ? 1 : -1));
    }
    return base;
  }, [category, sort]);

  const pages = totalPages(filtered.length, NEWS_PAGE_SIZE);
  const current = paginate(filtered, Math.min(page, pages), NEWS_PAGE_SIZE);

  const updateParams = (next: { sort?: string; cat?: string; page?: number }) => {
    const params = new URLSearchParams();
    const nextSort = next.sort ?? sort;
    const nextCat = next.cat ?? category;
    const nextPage = next.page ?? 1;
    if (nextSort !== "latest") params.set("sort", nextSort);
    if (nextCat !== "全部") params.set("cat", nextCat);
    if (nextPage > 1) params.set("page", String(nextPage));
    setSearchParams(params);
  };

  const hotList = [...newsList].sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, 4);

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteNavbar />

      <main className="flex-1 pt-16 md:pt-20">
        <section className="w-full border-b border-background-200/80 bg-background-100/60">
          <div className="mx-auto w-full max-w-page px-4 pb-8 pt-6 md:px-6 md:pb-10 md:pt-8">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
              <i className="ri-arrow-right-s-line text-sm text-foreground-400" />
              <span className="text-foreground-800">动漫资讯</span>
            </nav>
            <h1 className="mt-4 font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-3xl">
              最新动漫资讯
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-7 text-foreground-600">
              新番动态、制作访谈、剧情解析与行业观察。按时间或编辑推荐排序，快速掌握本周值得关注的动漫消息。
            </p>
          </div>
        </section>

        <div className="mx-auto w-full max-w-page px-4 py-8 md:px-6 md:py-10">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
            <div className="min-w-0">
              <div className="mb-5 flex flex-col gap-3 border-b border-background-200 pb-4">
                <div className="flex flex-wrap items-center gap-2">
                  {categories.map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => updateParams({ cat: item })}
                      className={[
                        "whitespace-nowrap rounded-full border px-3 py-1.5 text-xs transition-colors",
                        category === item
                          ? "border-primary-500 bg-primary-500 text-background-50"
                          : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600",
                      ].join(" ")}
                    >
                      {item}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm text-foreground-600">
                    共 <span className="font-heading font-bold text-foreground-950">{filtered.length}</span> 篇资讯
                  </p>
                  <div className="flex items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1">
                    {sortOptions.map((option) => (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => updateParams({ sort: option.key })}
                        className={[
                          "whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors",
                          sort === option.key
                            ? "bg-background-50 text-primary-600"
                            : "text-foreground-600 hover:text-foreground-900",
                        ].join(" ")}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {current.length > 0 ? (
                <>
                  <div className="flex flex-col gap-3 md:gap-4">
                    {current.map((news) => (
                      <NewsCard key={news.id} news={news} variant="row" />
                    ))}
                  </div>
                  <Pagination
                    page={Math.min(page, pages)}
                    total={pages}
                    onChange={(nextPage) => {
                      updateParams({ page: nextPage });
                      window.scrollTo({ top: 220, behavior: "smooth" });
                    }}
                  />
                </>
              ) : (
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100/50 px-6 py-20 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-500">
                    <i className="ri-newspaper-line" />
                  </span>
                  <h2 className="mt-5 font-heading text-lg font-bold text-foreground-950">暂无相关资讯</h2>
                  <p className="mt-2 max-w-sm text-sm leading-6 text-foreground-600">
                    这个分类下还没有内容，换个分类看看，或者回到全部资讯。
                  </p>
                  <button
                    type="button"
                    onClick={() => updateParams({ cat: "全部" })}
                    className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
                  >
                    <i className="ri-refresh-line" />
                    查看全部资讯
                  </button>
                </div>
              )}
            </div>

            <aside className="w-full">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                  <i className="ri-fire-line text-base text-primary-500" />
                  最近更新
                </h2>
                <div className="mt-4 flex flex-col gap-4">
                  {hotList.map((news, index) => (
                    <Link
                      key={news.id}
                      to={`/news/detail/${news.id}`}
                      className="group flex items-start gap-3"
                    >
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-background-100 font-heading text-[11px] font-bold text-foreground-500">
                        {index + 1}
                      </span>
                      <span className="min-w-0">
                        <span className="line-clamp-2 block text-sm font-medium leading-6 text-foreground-900 transition-colors group-hover:text-primary-600">
                          {news.title}
                        </span>
                        <span className="mt-1 block text-[11px] text-foreground-400">{news.date}</span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="mt-4 rounded-lg border border-background-200 bg-background-100/60 p-5">
                <h2 className="font-heading text-sm font-bold text-foreground-950">订阅更新</h2>
                <p className="mt-2 text-xs leading-6 text-foreground-600">
                  本站为演示内容站，不提供真实订阅推送。想找某部作品的资讯，可以直接搜索作品名称。
                </p>
                <Link
                  to="/search"
                  className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-xs font-semibold text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
                >
                  <i className="ri-search-line" />
                  搜索资讯关键词
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}